import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { loginAdmin, registerNewSchool } from '../repositories/authRepository';
import { auth } from '../firebase'; 
import { sendPasswordResetEmail } from "firebase/auth";
import TutorialDrawer from './TutorialDrawer';

interface LoginScreenProps {
    onLoginSuccess: (firestoreId: string, schoolName: string) => void;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
    const { t, i18n } = useTranslation();
    const [isRegistering, setIsRegistering] = useState(false);
    const [isTutorialOpen, setIsTutorialOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [schoolName, setSchoolName] = useState('');
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const isRTL = i18n.language === 'ar';
    

    useEffect(() => {
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    useEffect(() => {
        if (!isRegistering) {
            const savedEmail = localStorage.getItem('rememberedEmail');
            const savedSchoolId = localStorage.getItem('rememberedSchoolId');
            if (savedEmail && savedSchoolId) {
                setEmail(savedEmail);
                setSchoolId(savedSchoolId);
                setRememberMe(true);
            }
        }
    }, [isRegistering]);

    // Ajoute ce useEffect après tes autres useEffect (vers ligne 55)
useEffect(() => {
    const savedLanguage = localStorage.getItem('app_language');
    if (savedLanguage && ['fr', 'en', 'es', 'ar'].includes(savedLanguage)) {
        i18n.changeLanguage(savedLanguage);
    }
}, []);

// Modifie cette fonction (vers ligne 90)
const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('app_language', lng);
};

    const handleForgotPassword = async () => {
        if (!email) {
            alert("Email manquant / Missing Email / Correo faltante / البريد الإلكتروني مفقود");
            return;
        }
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email); 
            alert(t('alert_reset_link_sent') || "OK: Email envoyé");
        } catch (err: any) {
            console.error("Erreur Firebase détaillée:", err);
            alert(t('error_reset_failed') || "Erreur: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            if (isRegistering) {
                await registerNewSchool(schoolName, email, address, phone);
                alert(t('alert_registration_sent'));
                setIsRegistering(false);
            } else {
                const result = await loginAdmin(email, password, schoolId);
                if (result?.firestoreId) {
                    if (rememberMe) {
                        localStorage.setItem('rememberedEmail', email);
                        localStorage.setItem('rememberedSchoolId', schoolId);
                    }
                    onLoginSuccess(result.firestoreId, result.schoolName);
                }
            }
        } catch (err: any) {
            setError(err.message || t('error_auth_failed'));
        } finally {
            setLoading(false);
        }
    };
const getAppStoreBadge = () => {
    switch(i18n.language) {
        case 'ar': return "/App_Store_ar.png";
        case 'es': return "/App_Store_es.png";
        case 'en': return "/App_Store_en.png";
        default:   return "/App_Store_fr.png";
    }
}; // L'accolade de fermeture indispensable
    const getLangLabel = () => {
        switch(i18n.language) {
            case 'ar': return 'اختر اللغة';
            case 'en': return 'Choose language';
            case 'es': return 'Elegir idioma';
            default: return 'Choix de la langue';
        }
    };

    const getPlayStoreBadge = () => {
        switch(i18n.language) {
            case 'ar': return "https://play.google.com/intl/en_us/badges/static/images/badges/ar_badge_web_generic.png";
            case 'en': return "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";
            case 'es': return "https://play.google.com/intl/en_us/badges/static/images/badges/es_badge_web_generic.png";
            default: return "https://play.google.com/intl/en_us/badges/static/images/badges/fr_badge_web_generic.png";
        }
    };

    return (
        <div style={{...containerStyle, direction: isRTL ? 'rtl' : 'ltr'}}>
            <div style={cardStyle}>
                
                <div style={{
                    ...langSelectorContainerStyle,
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {/* TUTORIAL CLICABLE OUVRE LE DRAWER */}
                    <div 
                        onClick={() => setIsTutorialOpen(true)}
                        style={{
                            ...privacyLinkStyle, 
                            fontSize: isRTL ? '16px' : '14px',
                            color: '#dc0101',
                            fontWeight: '800',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        📖 {t('link_tutorial', "TUTORIAL")}
                    </div>

                    {/* SÉLECTEUR DE LANGUE À DROITE */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isRTL ? 'flex-start' : 'flex-end', gap: '2px' }}>
                        <label style={langLabelStyle}>{getLangLabel()}</label>
                        <select 
                            value={i18n.language} 
                            onChange={(e) => changeLanguage(e.target.value)}
                            style={selectStyle}
                        >                            
                            <option value="en">English</option>
                            <option value="fr">Français</option>
                            <option value="es">Español</option>
                            <option value="ar">العربية</option>
                        </select>
                    </div>
                </div>

                <img src="/dark-4x.png" alt="Logo GPSchoolBus" style={imageStyle} />
                <h2 style={{
                    ...titleStyle, 
                    fontSize: isRTL ? '1.2rem' : '0.95rem' 
                }}>
                    {isRegistering ? t('title_register') : t('title_login')}
                </h2>
                
                <form onSubmit={handleAuth}>
                    {isRegistering && (
                        <input style={inputStyle} placeholder={t('placeholder_school_name')} value={schoolName} onChange={e => setSchoolName(e.target.value)} required />
                    )}
                    {!isRegistering && (
                        <input style={inputStyle} placeholder={t('placeholder_school_id')} value={schoolId} onChange={e => setSchoolId(e.target.value)} required />
                    )}
                    <input style={inputStyle} type="email" placeholder={t('placeholder_email')} value={email} onChange={e => setEmail(e.target.value)} required />
                    {!isRegistering && (
                        <div style={{ position: 'relative', width: '100%', marginBottom: '10px' }}>
                            <input 
                                style={{
                                    ...inputStyle, 
                                    marginBottom: 0, 
                                    paddingRight: isRTL ? '12px' : '48px', 
                                    paddingLeft: isRTL ? '48px' : '12px'
                                }} 
                                type={showPassword ? "text" : "password"} 
                                placeholder={t('placeholder_password')} 
                                value={password} 
                                onChange={e => setPassword(e.target.value)} 
                                required 
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: isRTL ? 'unset' : '15px',
                                    left: isRTL ? '15px' : 'unset',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#5d5a88', 
                                    padding: '0px'
                                }}
                            >
                                {showPassword ? (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                    </svg>
                                ) : (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.01 2.54 2.9 4.59 5.25 5.75l-1.46 1.46 1.41 1.41 16.09-16.09-1.41-1.41-2.4 2.4C16.84 4.94 14.54 4.5 12 4.5zM12 17c-2.76 0-5-2.24-5-5 0-.77.18-1.5.49-2.14l1.57 1.57c-.03.18-.06.37-.06.57 0 1.66 1.34 3 3 3 .2 0 .39-.03.57-.06l1.57 1.57c-.64.31-1.37.49-2.14.49zm2.97-4.03l1.83 1.83c.13-.26.2-.55.2-.8 0-1.66-1.34-3-3-3-.25 0-.54.07-.8.2l1.83 1.83-.06-.06zM12 7c2.76 0 5 2.24 5 5 0 .77-.18 1.5-.49 2.14l1.57 1.57c.58-1.1 1.15-2.48 1.15-3.71 0-4.39-4.27-7.5-9-7.5-1.52 0-2.94.31-4.21.86l1.71 1.71C8.77 7.15 10.33 7 12 7z"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                    )}
                    {isRegistering && (
                        <>
                            <input style={inputStyle} placeholder={t('placeholder_address')} value={address} onChange={e => setAddress(e.target.value)} required />
                            <input style={inputStyle} placeholder={t('placeholder_phone')} value={phone} onChange={e => setPhone(e.target.value)} />
                        </>
                    )}

                    {!isRegistering && (
                        <div style={optionsStyle}>
                            <label style={{cursor:'pointer', display:'flex', alignItems:'center', gap: '5px'}}>
                                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} /> 
                                {t('label_remember_me')}
                            </label>
                            <span 
                                style={{color:'#3182ce', cursor:'pointer'}} 
                                onClick={handleForgotPassword}
                            >
                                {t('link_forgot_password')}
                            </span>
                        </div>
                    )}

                    {error && <div style={errorStyle}>{error}</div>}

                    <button type="submit" disabled={loading} style={{
                        ...buttonStyle,
                        fontSize: isRTL ? '1.1rem' : '0.9rem'
                    }}>
                        {loading ? t('btn_loading') : (isRegistering ? t('btn_submit_register') : t('btn_login'))}
                    </button>
                </form>

                <div style={{marginTop: '20px'}}>
                    <button 
                        onClick={() => {setIsRegistering(!isRegistering); setError('');}} 
                        style={{...registerButtonStyle, fontSize: isRTL ? '15px' : '11px'}}
                    >
                        {isRegistering ? t('link_back_to_login') : t('link_create_account')}
                    </button>
                </div>

                <div style={footerStyle}>
    <button 
        onClick={() => setShowPrivacy(true)} 
        style={{...privacyLinkStyle, fontSize: isRTL ? '14px' : '12px'}}
    >
        🛡️ {t('link_privacy_policy')}
    </button>
    
    <span style={{ margin: '0 8px', color: '#cbd5e0' }}>|</span>
    
    {/* NOUVEAU LIEN DE SUPPRESSION */}
    <button 
    onClick={() => window.location.href = '/data-safety'} 
    style={{...privacyLinkStyle, fontSize: isRTL ? '14px' : '12px', color: '#e53e3e'}}
>
    🗑️ {t('link_data_deletion', "Suppression des données")}
</button>
</div>
        
                {/* BLOC DES BADGES DE TÉLÉCHARGEMENT ALIGNÉS CÔTE À CÔTE */}
                {!isRegistering && (
                    <div style={{ 
    marginTop: '15px', 
    paddingTop: '15px', 
    borderTop: '1px solid #f1f5f9',
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    gap: '12px' 
}}>
    <div style={{ 
        display: 'flex', 
        flexDirection: isRTL ? 'row-reverse' : 'row', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '12px', 
        width: '100%' 
    }}>
        {/* Le commentaire doit être placé ICI, juste au-dessus de la balise, pas dans le href */}
<a 
                href="https://apps.apple.com/us/app/gpschoolbus-gps-school-bus/id6767029370" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ display: 'block', transition: 'transform 0.2s', transform: 'scale(1)' }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
            <img 
                src={getAppStoreBadge()} 
                alt="App Store" 
                style={{ 
                    width: i18n.language === 'ar' ? '125px' : (i18n.language === 'fr' ? '120px' : '115px'), 
                    height: 'auto',
                    display: 'block'
                }} 
            />
        </a>

        {/* Google Play */}
        <a 
            href="https://play.google.com/store/apps/details?id=com.aref.gpschoolbus" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'block', transition: 'transform 0.2s', transform: 'scale(1)' }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
            <img 
                src={getPlayStoreBadge()} 
                alt="Google Play" 
                style={{ 
                    width: (i18n.language === 'en' || i18n.language === 'ar') ? '150px' : '130px', 
                    height: 'auto',
                    display: 'block'
                }} 
            />
        </a>
    </div>   
</div>
                )}
            </div>

            {showPrivacy && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={modalHeaderStyle}>
                            <h2 style={{margin: 0, fontSize: '1.2rem', color: '#2d3748'}}>{t('charter_main_title')}</h2>
                            <button onClick={() => setShowPrivacy(false)} style={closeXStyle}>×</button>
                        </div>
                        
                        <div style={{...modalBodyStyle, textAlign: isRTL ? 'right' : 'left'}}>
                            <section style={{marginBottom: '20px'}}>
                                <h4 style={sectionTitleStyle}>1. {t('charter_s1_title')}</h4>
                                <p>
                                    <Trans i18nKey="charter_s1_text">
                                        <strong />
                                    </Trans>
                                </p>
                            </section>
                            <section style={{marginBottom: '20px'}}>
                                <h4 style={sectionTitleStyle}>2. {t('charter_s2_title')}</h4>
                                <p>{t('charter_s2_text')}</p>
                                <ul style={{paddingLeft: isRTL ? '0' : '20px', paddingRight: isRTL ? '20px' : '0', marginTop: '10px'}}>
                                    <li style={{marginBottom: '10px'}}>
                                        <Trans i18nKey="charter_s2_li1"><strong /></Trans>
                                    </li>
                                    <li>
                                        <Trans i18nKey="charter_s2_li2"><strong /></Trans>
                                    </li>
                                </ul>
                            </section>
                            <section style={{marginBottom: '20px'}}>
                                <h4 style={sectionTitleStyle}>3. {t('charter_s3_title')}</h4>
                                <p>
                                    <Trans i18nKey="charter_s3_text"><strong /></Trans>
                                </p>
                            </section>
                            <section style={{marginBottom: '20px'}}>
                                <h4 style={sectionTitleStyle}>4. {t('charter_s4_title')}</h4>
                                <p>{t('charter_s4_text')}</p>
                                <ul style={{paddingLeft: isRTL ? '0' : '20px', paddingRight: isRTL ? '20px' : '0', marginTop: '10px'}}>
                                    <li style={{marginBottom: '5px'}}>
                                        <Trans i18nKey="charter_s4_li1"><strong /></Trans>
                                    </li>
                                    <li>
                                        <Trans i18nKey="charter_s4_li2"><strong /></Trans>
                                    </li>
                                </ul>
                            </section>
                            <section>
                                <h4 style={sectionTitleStyle}>5. {t('charter_s5_title')}</h4>
                                <p>
                                    <Trans i18nKey="charter_s5_text"><strong /></Trans>
                                </p>
                            </section>
                        </div>
                        <button onClick={() => setShowPrivacy(false)} style={finalCloseButtonStyle}>{t('btn_accept_close')}</button>
                    </div>
                </div>
            )}

            {/* COMPOSANT TUTORIEL FINALISÉ */}
            <TutorialDrawer 
                isOpen={isTutorialOpen} 
                onClose={() => setIsTutorialOpen(false)} 
                isRTL={isRTL} 
            />
        </div>
    );
};

// --- STYLES ---
const sectionTitleStyle: React.CSSProperties = { color: '#2d3748', fontSize: '0.95rem', fontWeight: '800', marginBottom: '8px', textTransform: 'uppercase' };
const langSelectorContainerStyle: React.CSSProperties = { display: 'flex', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9', width: '100%' };
const langLabelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' };
const selectStyle: React.CSSProperties = { padding: '6px 10px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#fff', fontSize: '13px', fontWeight: '600', color: '#2d3748', cursor: 'pointer', outline: 'none' };
const containerStyle: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#ffc107', fontFamily: '"Segoe UI", Roboto, sans-serif' };
const cardStyle: React.CSSProperties = { backgroundColor: 'white', padding: '20px 40px 30px 45px', borderRadius: '40px', width: '90%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', textAlign: 'center' };
const imageStyle: React.CSSProperties = { width: '120px', marginBottom: '10px' };
const titleStyle: React.CSSProperties = { marginBottom: '15px', fontWeight: '800', color: '#2d3748', fontSize: '0.95rem', letterSpacing: '0.3px', textTransform: 'uppercase' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '12px', marginBottom: '10px', border: '1.5px solid #edf2f7', borderRadius: '15px', boxSizing: 'border-box', backgroundColor: '#f7fafc', fontSize: '0.9rem', textAlign: 'inherit' };
const buttonStyle: React.CSSProperties = { width: '100%', padding: '16px', backgroundColor: '#2d3748', color: 'white', border: 'none', borderRadius: '50px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' };
const registerButtonStyle: React.CSSProperties = { width: '100%', padding: '14px', backgroundColor: '#f8fafc', color: '#2d3748', border: '1px solid #e2e8f0', borderRadius: '15px', fontWeight: '700', cursor: 'pointer', fontSize: '11px', textTransform: 'uppercase', marginBottom: '0px' };
const optionsStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '15px', color: '#4a5568' };
const errorStyle: React.CSSProperties = { color: '#e53e3e', fontSize: '12px', marginBottom: '10px', padding: '8px', backgroundColor: '#fff5f5', borderRadius: '10px' };
const footerStyle: React.CSSProperties = { marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '8px' };
const privacyLinkStyle: React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: '600', textDecoration: 'none', padding: '0px' };
const modalOverlayStyle: React.CSSProperties = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '15px' };
const modalContentStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '35px', maxWidth: '650px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' };
const modalHeaderStyle: React.CSSProperties = { padding: '20px 30px', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const modalBodyStyle: React.CSSProperties = { padding: '25px 35px', overflowY: 'auto', color: '#4a5568', fontSize: '14px', lineHeight: '1.7' };
const closeXStyle: React.CSSProperties = { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#cbd5e0' };
const finalCloseButtonStyle: React.CSSProperties = { margin: '15px 40px 30px 40px', padding: '15px', backgroundColor: '#ffc107', border: 'none', borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', color: '#2d3748' };